import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import axiosInstance from '../../../utils/axios';
import { toast } from 'react-hot-toast';

const AuditTrailPanel = ({ filters }) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [trailData, setTrailData] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0
  });

  const loadTrail = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page: page,
        per_page: pagination.per_page,
        search: searchTerm,
        action: filterType !== 'all' ? filterType : undefined,
        ...filters
      };

      const response = await axiosInstance.get('/audit/trail', { params });
      
      if (response.data.success) {
        setTrailData(response.data.data.data || []);
        setPagination({
          current_page: response.data.data.current_page || 1,
          per_page: response.data.data.per_page || 20,
          total: response.data.data.total || 0
        });
      } else {
        toast.error('Failed to load audit trail');
      }
    } catch (error) {
      console.error('Error loading audit trail:', error);
      toast.error('Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterType, filters, pagination.per_page]);

  useEffect(() => {
    loadTrail();
  }, [loadTrail]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadTrail(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, loadTrail]);

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = (event) => {
      const newFilters = event.detail || {};
      if (Object.keys(newFilters).length > 0) {
        loadTrail(1);
      } else {
        loadTrail(pagination.current_page);
      }
    };
    window.addEventListener('refreshAuditView', handleRefresh);
    return () => window.removeEventListener('refreshAuditView', handleRefresh);
  }, [loadTrail, pagination.current_page]);

  const getActionIcon = (action) => {
    const actionLower = action?.toLowerCase() || '';
    if (actionLower.includes('verified')) return 'CheckCircle';
    if (actionLower.includes('bulk')) return 'RefreshCw';
    if (actionLower.includes('exception') || actionLower.includes('escalated')) return 'AlertTriangle';
    if (actionLower.includes('created')) return 'Plus';
    if (actionLower.includes('report')) return 'FileText';
    return 'Activity';
  };

  const getActionColor = (action) => {
    const actionLower = action?.toLowerCase() || '';
    if (actionLower.includes('verified')) return 'text-success';
    if (actionLower.includes('bulk')) return 'text-accent';
    if (actionLower.includes('exception') || actionLower.includes('escalated')) return 'text-warning';
    if (actionLower.includes('created')) return 'text-primary';
    if (actionLower.includes('report')) return 'text-muted-foreground';
    return 'text-muted-foreground';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return { date: 'N/A', time: 'N/A' };
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      })
    };
  };

  const actionTypes = [
    { value: 'all', label: 'All Actions' },
    { value: 'Asset Verified', label: 'Verified' },
    { value: 'Bulk Status Update', label: 'Bulk Update' },
    { value: 'Exception Escalated', label: 'Escalated' },
    { value: 'Asset Created', label: 'Created' },
    { value: 'Audit Report Generated', label: 'Report' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Audit Trail</h3>
            <p className="text-sm text-muted-foreground">
              Complete verification history for SOX compliance
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                const params = new URLSearchParams();
                params.append('type', 'trail');
                window.open(`/api/audit/export?${params.toString()}`, '_blank');
              }}
            >
              <Icon name="Download" size={16} className="mr-2" />
              Export Trail
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="flex-1 w-full sm:max-w-md">
            <Input
              type="search"
              placeholder="Search by Asset ID, User, or Action..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="w-full sm:w-48">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md text-sm bg-input text-foreground focus:ring-2 focus:ring-ring focus:border-ring"
            >
              {actionTypes.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          <div className="max-h-96 overflow-y-auto">
            <div className="divide-y divide-border">
              {trailData.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="History" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No audit trail entries found</p>
                </div>
              ) : (
                trailData.map((entry) => {
                  const timestamp = formatTimestamp(entry.action_timestamp);
                  const changes = entry.changes || {};
                  
                  return (
                    <div key={entry.id} className="p-6 hover:bg-muted/30 transition-smooth">
                      <div className="flex items-start space-x-4">
                        <div className={`mt-1 ${getActionColor(entry.action)}`}>
                          <Icon name={getActionIcon(entry.action)} size={20} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <div className="flex items-center space-x-3 flex-wrap gap-2">
                              <h4 className="font-medium text-card-foreground">{entry.action}</h4>
                              <span className="text-sm text-muted-foreground">
                                Asset: {entry.asset_id || 'System'}
                              </span>
                            </div>
                            <div className="text-right text-sm text-muted-foreground">
                              <div>{timestamp.date}</div>
                              <div>{timestamp.time}</div>
                            </div>
                          </div>
                          
                          <p className="text-sm text-muted-foreground mb-3">
                            {entry.details || 'No additional details'}
                          </p>
                          
                          {changes && Object.keys(changes).length > 0 && (
                            <div className="bg-muted/50 rounded-md p-3 mb-3">
                              <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                                Changes Made
                              </h5>
                              <div className="space-y-1">
                                {Object.entries(changes).map(([field, change]) => (
                                  <div key={field} className="flex items-center text-xs flex-wrap gap-1">
                                    <span className="font-medium text-card-foreground capitalize">
                                      {field.replace(/([A-Z])/g, ' $1').trim()}:
                                    </span>
                                    <span className="text-muted-foreground">
                                      {change?.from ? `"${change.from}"` : 'null'} → "{change?.to || 'null'}"
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                            <span>
                              <Icon name="User" size={12} className="inline mr-1" />
                              {entry.user_id} ({entry.user_role || 'User'})
                            </span>
                            {entry.ip_address && (
                              <span>
                                <Icon name="Globe" size={12} className="inline mr-1" />
                                {entry.ip_address}
                              </span>
                            )}
                            {entry.session_id && (
                              <span>Session: {entry.session_id}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Pagination */}
          {pagination.total > pagination.per_page && (
            <div className="p-4 border-t border-border flex items-center justify-between flex-wrap gap-4">
              <div className="text-sm text-muted-foreground">
                Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.current_page === 1}
                  onClick={() => loadTrail(pagination.current_page - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {pagination.current_page} of {Math.ceil(pagination.total / pagination.per_page)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.current_page >= Math.ceil(pagination.total / pagination.per_page)}
                  onClick={() => loadTrail(pagination.current_page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      
      <div className="p-4 border-t border-border bg-muted/20">
        <div className="flex items-center justify-between text-xs text-muted-foreground flex-wrap gap-2">
          <span>All times in UTC. Retention period: 7 years</span>
          {pagination.total > 0 && <span>{pagination.total} total entries</span>}
        </div>
      </div>
    </div>
  );
};

export default AuditTrailPanel;